import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Advent of Code Day 24 – The A* Algorithm, I think",
  "description": "",
  "published": "2017-12-25T00:00:00.000Z",
  "redirect_from": ["/blog/advent-code-day-24-algorithm-think/swizec/7998"],
  "categories": "Haskell, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`On Day 24 of Advent of Code we had to build a path finding algorithm. We're given a set of nodes and a heuristic and have to find the best possible path.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`OMG I DID IT! I built a recursive implementation of A* in Haskell all on my own for `}<a parentName="p" {...{
          "href": "https://twitter.com/hashtag/AdventOfCode?src=hash&ref_src=twsrc%5Etfw"
        }}>{`#AdventOfCode`}</a>{` Day 24.`}<br parentName="p"></br><br parentName="p"></br>{`Didn't even have to look it up 💪`}<br parentName="p"></br><br parentName="p"></br>{` `}<a parentName="p" {...{
          "href": "https://t.co/9j4Iq5VN1u"
        }}>{`pic.twitter.com/9j4Iq5VN1u`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/945176148358524929"
      }}>{`December 25, 2017`}</a></blockquote>
    <p>{`The puzzle uses different phrasing, but that's what it is.`}</p>
    <blockquote>
      <p parentName="blockquote">{`Build a bridge out of the magnetic components strewn about nearby.`}</p>
      <p parentName="blockquote">{`Each component has two ports, one on each end. The ports come in all different types, and only matching types can be connected. You take an inventory of the components by their port types (your puzzle input). Each port is identified by the number of pins it uses; more pins mean a stronger connection for your bridge. A 3/7 component, for example, has a type-3 port on one side, and a type-7 port on the other.`}</p>
      <p parentName="blockquote">{`Your side of the pit is metallic; a perfect surface to connect a magnetic, zero-pin port. Because of this, the first port you use must be of type 0. It doesn't matter what type of port you end with; your goal is just to make the bridge as strong as possible.`}</p>
      <p parentName="blockquote">{`The strength of a bridge is the sum of the port types in each component. For example, if your bridge is made of components 0/3, 3/7, and 7/4, your bridge has a strength of 0 3 3 7 7 4 = 24.`}</p>
    </blockquote>
    <p>{`You can model this as a graph path finding algorithm.`}</p>
    <p>{`Each component is a node. It connects to all other components with matching pin numbers. Our job is to find the most expensive path in this imaginary graph.`}</p>
    <p>{`Let's take the test input:`}</p>
    <pre><code parentName="pre" {...{}}>{`0/2
2/2
2/3
3/4
3/5
0/1
10/1
9/10
`}</code></pre>
    <p>{`With those nodes, our graph of connections looks like this 👇`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/12/advent-of-code-day-24-graph.jpg",
        "alt": null
      }}></img><img parentName="p" {...{
        "src": "graph",
        "alt": null
      }}></img></p>
    <p>{`Sorry that's not laid out the best and I don't have my usual colourful markers on me right now. But that's our graph.`}</p>
    <p>{`The correct solution in this case is: `}<inlineCode parentName="p">{`0/1--10/1--9/10`}</inlineCode>{` for a total strength of `}<inlineCode parentName="p">{`31`}</inlineCode>{`.`}</p>
    <h2 {...{
      "id": "my-a-implementation-in-haskell"
    }}>{`My A`}{`*`}{` implementation in Haskell`}</h2>
    <p><a parentName="p" {...{
        "href": "https://github.com/Swizec/advent-of-code-2017/blob/master/24.hs"
      }}>{`Full code on Github`}</a></p>
    <p>{`To find that solution we can use the famous `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/A*_search_algorithm"
      }}>{`A`}{`*`}{` algorithm`}</a>{`, which is itself an improvement on the even more famous `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Dijkstra%27s_algorithm"
      }}>{`Dijkstra algorithm`}</a>{`. This is the stuff I used to nerd out on in college. 😇`}</p>
    <p>{`Now I can't promise my algorithm is a true implementation of A`}{`*`}{` `}<em parentName="p">{`or`}</em>{` of Dijkstra's algorithm. I used what I remembered from college as inspiration and derived the search algorithm from scratch all on my own.`}</p>
    <p><img parentName="p" {...{
        "src": "/1e9a6f38b7c3c57a904459f40afd9d1d/A-search-algorithm-visualization63i2d3.gif",
        "alt": "A* search algorithm visualization"
      }}></img></p>
    <p>{`A`}{`*`}{` search algorithm visualization`}</p>
    <p>{`It was so much fun that it kept me from my Christmas duties and my girlfriend complained. A lot.`}</p>
    <p>{`The core of my solution is the recursive `}<inlineCode parentName="p">{`buildBridge`}</inlineCode>{` function. It builds bridges (graph paths) from all possible candidates for the next bridge piece, then chooses the best one.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`-- build bridge with maximum score
buildBridge::Int -> [(Int, Int)] -> [(Int, Int)] -> [(Int, Int)]
buildBridge port [] bridge = bridge
buildBridge port pool bridge
    | length opts > 0 = maximumBy (comparing heuristic) $ map (\\(nextPort, link) ->
        buildBridge nextPort (poolWithout link pool) (bridge ++ [link])) opts
    | otherwise = bridge
    where opts = candidates port pool []

heuristic::[(Int, Int)] -> Int
heuristic links = sum [a b | (a, b) <- links]
`}</code></pre>
    <p>{`That's not the prettiest Haskell code. Here's what it means:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`buildBridge`}</inlineCode>{` is a function that takes a number, two lists of `}<inlineCode parentName="li">{`(number, number)`}</inlineCode>{` tuples, and returns a list of `}<inlineCode parentName="li">{`(number, number)`}</inlineCode>{` tuples. The number is the `}<inlineCode parentName="li">{`port`}</inlineCode>{` we're trying to build bridges for, the first list is a `}<inlineCode parentName="li">{`pool`}</inlineCode>{` of available components, and the 3rd list is the current `}<inlineCode parentName="li">{`bridge`}</inlineCode>{` we're extending.`}</li>
      <li parentName="ul">{`if the `}<inlineCode parentName="li">{`pool`}</inlineCode>{` of available components is empty, return the `}<inlineCode parentName="li">{`bridge`}</inlineCode></li>
      <li parentName="ul">{`build a list of `}<inlineCode parentName="li">{`candidates`}</inlineCode>{` for the next component and put it in `}<inlineCode parentName="li">{`opts`}</inlineCode></li>
      <li parentName="ul">{`if `}<inlineCode parentName="li">{`opts`}</inlineCode>{` has a length greater than zero, return the best bridge where you `}<inlineCode parentName="li">{`buildBridge`}</inlineCode>{` for each candidate component`}</li>
      <li parentName="ul">{`if there are no available candidates, return the `}<inlineCode parentName="li">{`bridge`}</inlineCode></li>
      <li parentName="ul">{`the `}<inlineCode parentName="li">{`heuristic`}</inlineCode>{` function sums all ports in the bridge`}</li>
    </ul>
    <p>{`Each time we go into a recursion, we take components out of the component pool that's passed into `}<inlineCode parentName="p">{`buildBridge`}</inlineCode>{`. That guarantees we don't accidentally use a component multiple times.`}</p>
    <p>{`I find it difficult to visualize how this algorithm works. 😅 It's something between a breadth first search and Dijkstra's algorithm. Not actually sure it's A`}{`*`}{` after all 🤔`}</p>
    <h2 {...{
      "id": "how-it-works"
    }}>{`How it works`}</h2>
    <p>{`For every node, we find all possible nodes we can connect to (its neighbors). For each of those we build a path all the way to the end. This gives us all possible paths through the graph.`}</p>
    <p>{`Then we unwind the recursion to collapse them into the best possible path.`}</p>
    <p>{`For the test input, all possible exhaustive paths are:`}</p>
    <pre><code parentName="pre" {...{}}>{`0/1--10/1--9/10
0/2--2/3--3/4
0/2--2/3--3/5
0/2--2/2--2/3--3/4
0/2--2/2--2/3--3/5
`}</code></pre>
    <p>{`As our recursion unwinds, it picks the best path based on our heuristic function. You can think of that process as replacing nodes with their values and picking the best option.`}</p>
    <pre><code parentName="pre" {...{}}>{`# Step 1
0/1--10/1--19
0/2--2/3--7
0/2--2/3--8
0/2--2/2--2/3--7
0/2--2/2--2/3--8

# Step 2
0/1--30
0/2--13
0/2--2/2--13

# Step 3
31
15
0/2--17

# Step 4
31
15
19

Step 5
31
`}</code></pre>
    <p>{`At each step of unwinding, we can discard low value alternatives when they share the same bridge root. Eventually we end up with a single possibility.`}</p>
    <p>{`This `}<em parentName="p">{`sounds`}</em>{` a lot like the description of A`}<em parentName="p">{`, but I'm honestly not sure that my algorithm is A`}</em>{`. 😕`}</p>
    <h2 {...{
      "id": "the-helper-functions"
    }}>{`The helper functions`}</h2>
    <p>{`Either way, to find those `}<inlineCode parentName="p">{`candidates`}</inlineCode>{`, we use the `}<inlineCode parentName="p">{`candidates`}</inlineCode>{` function:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`-- find all possible candidates for next link
candidates::Int -> [(Int, Int)] -> [(Int, (Int, Int))] -> [(Int, (Int, Int))]
candidates port [] acc = acc
candidates port pool acc
    | (-1, -1) == link = acc
    | otherwise = acc    [(nextPort, link)] ++ (candidates port nextPool acc)
    where (nextPort, link) = findNext port pool
          nextPool = poolWithout link pool
`}</code></pre>
    <p>{`This function takes a `}<inlineCode parentName="p">{`port`}</inlineCode>{` we're connecting to, a `}<inlineCode parentName="p">{`pool`}</inlineCode>{` of components, and the current known list of results, `}<inlineCode parentName="p">{`acc`}</inlineCode>{`.`}</p>
    <p>{`We use `}<inlineCode parentName="p">{`findNext`}</inlineCode>{` to find the next component we can use, called `}<inlineCode parentName="p">{`link`}</inlineCode>{`. Link because it's going to be a link in the bridge. We take it out of the `}<inlineCode parentName="p">{`nextPool`}</inlineCode>{` of components passed into recursion using `}<inlineCode parentName="p">{`poolWithout`}</inlineCode>{`.`}</p>
    <p>{`If the `}<inlineCode parentName="p">{`link`}</inlineCode>{` was found, we expand our known list of candidates, `}<inlineCode parentName="p">{`acc`}</inlineCode>{`, with the new link and a call to `}<inlineCode parentName="p">{`candidates`}</inlineCode>{` with the remaining pool.`}</p>
    <p>{`You may be wondering why we're spending so much time passing ports around instead of nodes. It's because each node is made out of two ports and each port can only be used once. We try to keep track of that.`}</p>
    <p>{`For instance when we look for candidates to connect to `}<inlineCode parentName="p">{`(0, 1)`}</inlineCode>{` we know that `}<inlineCode parentName="p">{`0`}</inlineCode>{` is already used. So we look for anything that can connect to `}<inlineCode parentName="p">{`1`}</inlineCode>{`. When we find `}<inlineCode parentName="p">{`(1,2)`}</inlineCode>{` and `}<inlineCode parentName="p">{`(3,1)`}</inlineCode>{` we have to note that `}<inlineCode parentName="p">{`1`}</inlineCode>{` is used up so the next node will have to connect to either `}<inlineCode parentName="p">{`2`}</inlineCode>{` or `}<inlineCode parentName="p">{`3`}</inlineCode>{`.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`findNext`}</inlineCode>{` function is where this finding happens.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`-- find next link in bridge and which port to use for next next link
findNext::Int -> [(Int, Int)] -> (Int, (Int, Int))
findNext port [] = (port, (-1,-1))
findNext port pool
    | port == left = (right, (left, right))
    | port == right = (left, (left, right))
    | otherwise = findNext port (tail pool)
    where (left, right) = head pool
`}</code></pre>
    <p><inlineCode parentName="p">{`findNext`}</inlineCode>{` returns the first matching component from our pool. When the pool is empty, it returns `}<inlineCode parentName="p">{`(-1, -1)`}</inlineCode>{` to signify nothing was found.`}</p>
    <p>{`When the `}<inlineCode parentName="p">{`port`}</inlineCode>{` matches either `}<inlineCode parentName="p">{`left`}</inlineCode>{` or `}<inlineCode parentName="p">{`right`}</inlineCode>{` side of the component, we return that component and the next port it can match to. If `}<inlineCode parentName="p">{`left`}</inlineCode>{` matched, we return `}<inlineCode parentName="p">{`(right, (component))`}</inlineCode>{`, if `}<inlineCode parentName="p">{`right`}</inlineCode>{` matched, then we return `}<inlineCode parentName="p">{`(left, (component))`}</inlineCode>{`.`}</p>
    <p>{`If nothing matched and there's still stuff in the pool, we return whatever `}<inlineCode parentName="p">{`findNext`}</inlineCode>{` finds in the pool without the first element. Because we checked the first element just now.`}</p>
    <p>{`Recursion 🤙`}</p>
    <p>{`The `}<inlineCode parentName="p">{`poolWithout`}</inlineCode>{` function we used in a couple places is a simple `}<inlineCode parentName="p">{`filter`}</inlineCode>{` by they way 👇`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`poolWithout::(Int, Int) -> [(Int, Int)] -> [(Int, Int)]
poolWithout link pool = filter (\\x -> link /= x) pool
`}</code></pre>
    <h2 {...{
      "id": "and-that-works"
    }}>{`And that works`}</h2>
    <p>{`That mass of recursion that's hard to visualize in your mind works. It really does. I'm kinda surprised. It feels like magic.`}</p>
    <p>{`I mean, I wrote the algorithm. I derived it from scratch. And when I try to think about how it works my mind just goes`}</p>
    <p><img parentName="p" {...{
        "src": "/0c119d1e5901e83563072eb67774c035/r-2013-10-tim-and-eric-mind-blown.gif",
        "alt": null
      }}></img></p>
    <p>{`Recursion is hard, okay.`}</p>
    <p>{`So did I build A`}{`*`}{` or not? This is gonna bother me 😕`}</p>
    <h2 {...{
      "id": "ps-star-2"
    }}>{`PS: star 2`}</h2>
    <p>{`For Star 2, we had to find the longest possible bridge with the best score. Same algorithm, different heuristic function.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`heuristic2::[(Int, Int)] -> [(Int, Int)] -> Ordering
heuristic2 a b
        | la > lb = LT
        | la > lb = GT
        | la == lb = compare (heuristic a) (heuristic b)
        where la = length a
              lb = length b
`}</code></pre>
    <p>{`Compare lengths, if lengths are the same, use the previous heuristic function based on strength.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      